import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createSupabaseServerClient } from "./ads.server";
import {
  ABOUT_MAX,
  companySlug,
  findForbiddenInAbout,
  normalizeOrgNumber,
  validateOrgNumber,
} from "./business";
import { limitsFor } from "./plans";
import { readPlanAndUsage } from "./plans.server";
import { enforceRateLimit } from "./rate-limit.server";


const SaveInput = z.object({
  accountType: z.enum(["private", "business"]),
  companyName: z.string().trim().max(120).optional(),
  orgNumber: z.string().trim().max(20).optional(),
  vatNumber: z.string().trim().max(30).optional(),
  invoiceAddress: z.string().trim().max(160).optional(),
  invoiceZip: z.string().trim().max(12).optional(),
  invoiceCity: z.string().trim().max(80).optional(),
  website: z.string().trim().max(200).optional(),
  about: z.string().trim().max(ABOUT_MAX).optional(),
  rightOfWithdrawal: z.string().trim().max(2000).optional(),
  warrantyPolicy: z.string().trim().max(2000).optional(),
  returnPolicy: z.string().trim().max(2000).optional(),
  taxRegistrations: z
    .object({
      moms: z.boolean().nullable(),
      fSkatt: z.boolean().nullable(),
      arbetsgivaravgift: z.boolean().nullable(),
    })
    .nullish(),
});

/**
 * Slår upp ett organisationsnummer. Bolagsverket är alltid först och avgör om
 * bolaget finns; Allabolag används bara för att fylla på övriga uppgifter.
 */
export const lookupCompany = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ orgNumber: z.string().trim().min(6).max(20) }).parse(input),
  )
  .handler(async ({ data }) => {
    const orgError = validateOrgNumber(data.orgNumber);
    if (orgError) return { ok: false as const, message: orgError };

    const { lookupOrgNumber } = await import("./org-lookup.server");
    const official = await lookupOrgNumber(data.orgNumber);
    if (official.status === "not_found" || official.status === "deregistered") {
      return { ok: false as const, message: official.message ?? "Företaget hittades inte.", official };
    }

    // Bara om registret svarade OK går vi vidare och berikar uppgifterna.
    const { lookupAllabolag } = await import("./allabolag-lookup.server");
    const extra = official.status === "verified" ? await lookupAllabolag(data.orgNumber) : null;

    // Webbplats föreslås alltid via en riktig webbsökning – aldrig katalogsajter.
    const { suggestWebsite, isDirectoryUrl } = await import("./website-suggest.server");
    const name = official.name ?? extra?.companyName ?? "";
    const scraped = extra?.website && !isDirectoryUrl(extra.website) ? extra.website : null;
    const websiteSuggestion = scraped || (await suggestWebsite(name));


    return { ok: true as const, official, extra, websiteSuggestion: websiteSuggestion || null };
  });

/** Hämtar kontotyp och eventuella företagsuppgifter för den inloggade. */
export const getMyBusinessProfile = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const [{ data: profile }, { data: biz }] = await Promise.all([
      context.supabase
        .from("profiles")
        .select("account_type")
        .eq("user_id", context.userId)
        .maybeSingle(),
      context.supabase
        .from("business_profiles")
        .select("*")
        .eq("user_id", context.userId)
        .maybeSingle(),
    ]);

    return {
      accountType: (profile?.account_type ?? "private") as "private" | "business",
      business: biz
        ? {
            companyName: biz.company_name,
            orgNumber: biz.org_number,
            vatNumber: biz.vat_number ?? "",
            invoiceAddress: biz.invoice_address ?? "",
            invoiceZip: biz.invoice_zip ?? "",
            invoiceCity: biz.invoice_city ?? "",
            website: biz.website ?? "",
            about: biz.about ?? "",
            slug: biz.slug,
            rightOfWithdrawal: biz.right_of_withdrawal ?? "",
            warrantyPolicy: biz.warranty_policy ?? "",
            returnPolicy: biz.return_policy ?? "",
            verificationStatus: (biz.verification_status ?? "unverified") as string,
            verifiedName: biz.verified_name ?? null,
            verifiedAt: biz.verified_at ?? null,
            reviewStatus: (biz.review_status ?? "pending") as string,
            vatRegistered: (biz as { vat_registered?: boolean | null }).vat_registered ?? null,
            taxRegistrations: ((biz as { tax_registrations?: unknown }).tax_registrations ?? null) as
              | { moms: boolean | null; fSkatt: boolean | null; arbetsgivaravgift: boolean | null }
              | null,
            reviewIssues: (biz.review_issues ?? []) as {
              field: string;
              severity: string;
              message: string;
            }[],
          }
        : null,
    };
  });

/** Sparar kontotyp och företagsuppgifter. Byte till Privat behåller uppgifterna men döljer dem. */
export const saveMyBusinessProfile = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => SaveInput.parse(input))
  .handler(async ({ data, context }) => {
    if (data.accountType === "private") {
      const { error } = await context.supabase
        .from("profiles")
        .upsert({ user_id: context.userId, account_type: "private" }, { onConflict: "user_id" });
      if (error) throw new Error(error.message);
      return { ok: true as const, accountType: "private" as const };
    }

    const companyName = (data.companyName ?? "").trim();
    if (companyName.length < 2) throw new Error("Fyll i företagets namn.");
    const orgError = validateOrgNumber(data.orgNumber ?? "");
    if (orgError) throw new Error(orgError);
    if (data.website && !/^https?:\/\//i.test(data.website)) {
      throw new Error("Webbplatsen ska börja med https://");
    }

    // Kontroll mot företagsregistret + AI-granskning innan uppgifterna sparas.
    const [{ lookupOrgNumber, sameCompanyName }, { reviewBusinessProfile }] = await Promise.all([
      import("./org-lookup.server"),
      import("./business-review.server"),
    ]);
    const lookup = await lookupOrgNumber(data.orgNumber ?? "");
    if (lookup.status === "not_found" || lookup.status === "deregistered") {
      throw new Error(lookup.message ?? "Organisationsnumret kunde inte verifieras.");
    }
    const nameMismatch =
      lookup.status === "verified" && !!lookup.name && !sameCompanyName(lookup.name, companyName);
    if (nameMismatch) {
      throw new Error(
        `Namnet stämmer inte med registret. Registrerat namn är "${lookup.name}".`,
      );
    }

    const review = await reviewBusinessProfile(
      {
        companyName,
        about: data.about ?? "",
        rightOfWithdrawal: data.rightOfWithdrawal ?? "",
        warrantyPolicy: data.warrantyPolicy ?? "",
        returnPolicy: data.returnPolicy ?? "",
      },
      context.userId,
    );
    if (review.verdict === "block") {
      return {
        ok: false as const,
        accountType: "business" as const,
        review,
        verification: lookup,
      };
    }

    // Slugen ska vara unik. Finns namnet redan lägger vi på en siffra.
    const base = companySlug(companyName) || "foretag";
    const { data: existing } = await context.supabase
      .from("business_profiles")
      .select("user_id, slug")
      .like("slug", `${base}%`);
    const taken = new Set(
      (existing ?? []).filter((r) => r.user_id !== context.userId).map((r) => r.slug),
    );
    const mine = (existing ?? []).find((r) => r.user_id === context.userId)?.slug;
    let slug = mine && mine.startsWith(base) ? mine : base;
    if (taken.has(slug)) {
      let i = 2;
      while (taken.has(`${base}-${i}`)) i++;
      slug = `${base}-${i}`;
    }

    const { error } = await context.supabase.from("business_profiles").upsert(
      {
        user_id: context.userId,
        company_name: companyName,
        org_number: normalizeOrgNumber(data.orgNumber ?? ""),
        vat_number: data.vatNumber || null,
        invoice_address: data.invoiceAddress || null,
        invoice_zip: data.invoiceZip || null,
        invoice_city: data.invoiceCity || null,
        website: data.website || null,
        about: data.about || null,
        right_of_withdrawal: data.rightOfWithdrawal || null,
        warranty_policy: data.warrantyPolicy || null,
        return_policy: data.returnPolicy || null,
        slug,
        verification_status: lookup.status === "verified" ? "verified" : "unverified",
        verified_name: lookup.status === "verified" ? (lookup.name ?? null) : null,
        verified_at: lookup.status === "verified" ? new Date().toISOString() : null,
        verification_source: lookup.source,
        review_status: review.verdict,
        review_issues: review.issues,
        reviewed_at: new Date().toISOString(),
        vat_registered: data.taxRegistrations ? data.taxRegistrations.moms : undefined,
        tax_registrations: data.taxRegistrations ?? undefined,
        tax_checked_at: data.taxRegistrations ? new Date().toISOString() : undefined,
      },
      { onConflict: "user_id" },
    );
    if (error) throw new Error(error.message);

    const { error: profileError } = await context.supabase
      .from("profiles")
      .upsert({ user_id: context.userId, account_type: "business" }, { onConflict: "user_id" });
    if (profileError) throw new Error(profileError.message);

    return {
      ok: true as const,
      accountType: "business" as const,
      slug,
      review,
      verification: lookup,
    };
  });

/**
 * Förfinar "Om företaget" med AI. Räknas mot samma månadskvot som
 * AI-omskrivningar av annonser.
 */
export const refineBusinessAbout = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        companyName: z.string().trim().min(1).max(120),
        about: z.string().trim().min(1).max(ABOUT_MAX),
        website: z.string().trim().max(200).optional(),
      })
      .parse(input),
  )
  .handler(async ({ data, context }) => {
    await enforceRateLimit("ai_generate", context.userId);

    const { plan, unlimited } = await readPlanAndUsage(context.supabase as never, context.userId);
    const limit = limitsFor(plan, unlimited).aiRewritesPerMonth;
    const since = new Date();
    since.setDate(since.getDate() - 30);
    const { count } = await context.supabase
      .from("ad_rewrites")
      .select("id", { count: "exact", head: true })
      .eq("user_id", context.userId)
      .gte("created_at", since.toISOString());
    const used = count ?? 0;
    if (limit !== null && used >= limit) {
      throw new Error(
        plan === "free"
          ? `Du har använt dina ${limit} AI-omskrivningar den här månaden. Uppgradera till Plus för 40 per månad.`
          : `Du har använt dina ${limit} AI-omskrivningar den här månaden. Uppgradera till Pro för obegränsat.`,
      );
    }

    const forbidden = findForbiddenInAbout(data.about);
    if (forbidden.length) {
      throw new Error("Ta bort länkar, e-post och telefonnummer ur texten innan du förfinerar den med AI.");
    }

    const { refineBusinessAbout: refine } = await import("./business-refine.server");
    const result = await refine(
      { companyName: data.companyName, about: data.about, website: data.website },
      context.userId,
    );

    await context.supabase.from("ad_rewrites").insert({ user_id: context.userId });

    return { about: result.about, remaining: limit === null ? null : Math.max(0, limit - used - 1) };
  });

/** Publik företagsprofil med företagets aktiva annonser. */
export const getBusinessProfile = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => z.object({ slug: z.string().min(1).max(80) }).parse(input))
  .handler(async ({ data }) => {
    const supabase = createSupabaseServerClient();
    // Publika fält hämtas via en databasfunktion som bara servern får anropa, så att
    // fakturaadress och andra känsliga uppgifter aldrig kan läsas av utomstående.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows } = await supabaseAdmin.rpc("get_public_business_profile", {
      _slug: data.slug,
    });
    const biz = Array.isArray(rows) ? rows[0] : rows;
    if (!biz) throw new Error("Företaget finns inte");

    const [{ data: profile }, { data: ads }, { data: hoursRow }] = await Promise.all([
      supabase.from("profiles").select("display_name").eq("user_id", biz.user_id).maybeSingle(),
      supabase
        .from("ads")
        .select(
          "slug, title, price, image_url, image_urls, category, location, created_at, sale_kind, auction_ends_at",
        )
        .eq("user_id", biz.user_id)
        .eq("is_paused", false)
        .is("archived_at", null)
        .is("deleted_at", null)
        .order("created_at", { ascending: false })
        .limit(60),
      supabaseAdmin.from("business_profiles").select("opening_hours").eq("user_id", biz.user_id).maybeSingle(),
    ]);

    return {
      company: {
        name: biz.company_name,
        orgNumber: biz.org_number,
        vatNumber: biz.vat_number,
        website: biz.website,
        logoUrl: biz.logo_url,
        slug: biz.slug,
        about: biz.about,
        verificationStatus: (biz as { verification_status?: string }).verification_status ?? "unverified",
        verifiedName: (biz as { verified_name?: string | null }).verified_name ?? null,
        rightOfWithdrawal: biz.right_of_withdrawal,
        warrantyPolicy: biz.warranty_policy,
        returnPolicy: biz.return_policy,
        contactName: profile?.display_name ?? biz.company_name,
        since: biz.created_at,
        openingHours: ((hoursRow as any)?.opening_hours ?? null) as Record<string, { closed: boolean; open: string; close: string }> | null,
      },
      ads: ads ?? [],
    };
  });

/** Publik katalog över företag med profil på Sellavo. */
export const listBusinessProfiles = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) =>
    z
      .object({ q: z.string().trim().max(60).nullish() })
      .parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    let query = supabaseAdmin
      .from("business_profiles")
      .select("company_name, slug, logo_url, about, website, verification_status, created_at, user_id")
      .not("slug", "is", null)
      .order("company_name", { ascending: true })
      .limit(300);
    if (data.q) query = query.ilike("company_name", `%${data.q}%`);
    const { data: rows } = await query;

    const list = (rows ?? []) as any[];
    const ids = list.map((b) => b.user_id);
    const counts = new Map<string, number>();
    if (ids.length) {
      const { data: ads } = await supabaseAdmin
        .from("ads")
        .select("user_id")
        .in("user_id", ids)
        .eq("is_paused", false)
        .eq("hidden", false)
        .is("archived_at", null)
        .is("deleted_at", null)
        .is("sold_at", null);
      for (const a of (ads ?? []) as { user_id: string }[]) {
        counts.set(a.user_id, (counts.get(a.user_id) ?? 0) + 1);
      }
    }

    return list.map((b) => ({
      name: b.company_name as string,
      slug: b.slug as string,
      logoUrl: (b.logo_url ?? null) as string | null,
      about: (b.about ?? null) as string | null,
      website: (b.website ?? null) as string | null,
      verified: b.verification_status === "verified",
      since: b.created_at as string,
      adCount: counts.get(b.user_id) ?? 0,
    }));
  });

/** Adminlista med alla företagsprofiler, inklusive obekräftade. */
export const adminListBusinesses = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("is_admin", { _user_id: context.userId });
    if (!isAdmin) throw new Error("Behörighet saknas.");

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: rows } = await supabaseAdmin
      .from("business_profiles")
      .select(
        "user_id, company_name, org_number, slug, website, verification_status, verified_name, verified_at, created_at",
      )
      .order("created_at", { ascending: false })
      .limit(500);
    return (rows ?? []) as any[];
  });
